import { useMutation } from "@apollo/client";
import { pick } from "lodash";
import { useMemo, useState } from "react";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  FeedbackState,
  GoalProgressType,
  GoalState,
  GoalVisibility,
  LinkArtifactToChatMessageMutationMutation,
  LinkArtifactToChatMessageMutationMutationVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import ArtifactCreationDialog, {
  ArtifactCreationDialogFormType,
} from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import linkArtifactToChatMessageMutation from "@apps/ask-ai/graphql/link-artifact-to-chat-message-mutation";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import CoreValueIcon from "@components/core-value-icon/core-value-icon";
import DraftLabel from "@components/draft-label/draft-label";
import GoalIcon from "@components/goal-icon/goal-icon";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { graphqlNone } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

const AskAISuggestedArtifact = ({
  suggestedArtifact,
  chatSessionId,
  messageId,
}: {
  suggestedArtifact: any;
  chatSessionId: number | null;
  messageId: string;
}) => {
  const [showArtifactDialog, setShowArtifactDialog] =
    useState<null | Partial<ArtifactCreationDialogFormType>>(null);
  const currentOrganization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const label = useLabel();
  const cannotBeSelfAssigned =
    suggestedArtifact.artifactType === ArtifactType.Recognition ||
    suggestedArtifact.artifactType === ArtifactType.Feedback;

  const assignee =
    suggestedArtifact.suggestedAssignee ||
    (cannotBeSelfAssigned ? undefined : currentUser);
  const [linkSuggestedArtifact, { loading: loadingLink }] = useMutation<
    LinkArtifactToChatMessageMutationMutation,
    LinkArtifactToChatMessageMutationMutationVariables
  >(linkArtifactToChatMessageMutation);

  const handleCloseDialog = (
    createdArtifact?: CreatedArtifactFragmentFragment
  ) => {
    setShowArtifactDialog(null);
    if (chatSessionId && createdArtifact) {
      linkSuggestedArtifact({
        variables: {
          artifactId: createdArtifact.id,
          chatSessionId: chatSessionId,
          messageId: messageId,
        },
      });
    }
  };

  const organization = currentOrganizationVar();
  const coreValues = assertEdgesNonNull(organization.coreValues);
  const suggestedCoreValue =
    (suggestedArtifact.coreValue &&
      coreValues.find(
        (coreValue) => coreValue.id === suggestedArtifact.coreValue.id
      )) ||
    coreValues[0];

  const suggestedKeyResults = (
    suggestedArtifact.artifactType === ArtifactType.Goal
      ? suggestedArtifact.keyResults || []
      : []
  ).map((kr: string) => ({
    uuid: uuidv4(),
    title: kr,
    startValue: 0,
    targetValue: 100,
    assignee: assignee,
    progressType: GoalProgressType.Percentage,
  }));

  const formOptions = useMemo(() => {
    return {
      title: suggestedArtifact.title,
      artifactType: suggestedArtifact.artifactType,
      organizationId: currentOrganization.id,
      assignee:
        suggestedArtifact.artifactType === ArtifactType.ActionItem &&
        assignee?.id &&
        assignee.id !== graphqlNone
          ? assignee
          : undefined,
      // Recognition
      recognitionRecipients:
        suggestedArtifact.artifactType === ArtifactType.Recognition &&
        assignee?.id &&
        assignee.id !== graphqlNone
          ? [assignee]
          : [],
      recognitionCoreValueId: suggestedCoreValue?.id,
      // Action Item
      actionItemDueDate:
        suggestedArtifact.artifactType === ArtifactType.ActionItem &&
        suggestedArtifact.dueDate
          ? suggestedArtifact.dueDate
          : undefined,
      // Feedback
      feedbackMessage:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? JSON.stringify({
              type: "doc",
              content: [
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: suggestedArtifact.description },
                  ],
                },
              ],
            })
          : undefined,
      feedbackRecipients:
        suggestedArtifact.artifactType === ArtifactType.Feedback &&
        assignee?.id &&
        assignee.id !== graphqlNone
          ? [assignee]
          : [],
      feedbackRecipientsCanView:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? suggestedArtifact.recipientsCanView
          : undefined,
      feedbackCreatorCanView:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? suggestedArtifact.creatorCanView
          : undefined,
      feedbackRecipientsManagersCanView:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? suggestedArtifact.recipientsManagersCanView
          : undefined,
      feedbackAdminsCanView:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? suggestedArtifact.adminsCanView
          : undefined,
      feedbackState:
        suggestedArtifact.artifactType === ArtifactType.Feedback
          ? suggestedArtifact.isDraft
            ? FeedbackState.Draft
            : FeedbackState.Sent
          : undefined,
      // Goal
      owners:
        suggestedArtifact.artifactType === ArtifactType.Goal &&
        assignee?.id &&
        assignee.id !== graphqlNone
          ? [assignee]
          : [],
      scope:
        suggestedArtifact.artifactType === ArtifactType.Goal
          ? suggestedArtifact.scope
          : undefined,
      goalState:
        suggestedArtifact.artifactType === ArtifactType.Goal
          ? suggestedArtifact.state
          : undefined,
      goalVisibility:
        suggestedArtifact.artifactType === ArtifactType.Goal
          ? suggestedArtifact.visibility
          : undefined,
      keyResults: suggestedKeyResults,
    };
  }, [
    suggestedArtifact,
    assignee,
    suggestedCoreValue,
    currentOrganization,
    suggestedKeyResults,
  ]);

  const assigneeName = useMemo(() => {
    return assignee?.name || "";
  }, [assignee]);

  const title = useMemo(() => {
    return suggestedArtifact.artifactType === ArtifactType.Recognition &&
      suggestedCoreValue
      ? suggestedCoreValue.title
      : suggestedArtifact.title;
  }, [suggestedArtifact, suggestedCoreValue]);

  const description = useMemo(() => {
    return suggestedArtifact.artifactType === ArtifactType.Recognition
      ? suggestedArtifact.title
      : suggestedArtifact.description;
  }, [suggestedArtifact]);
  return (
    <>
      {showArtifactDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={showArtifactDialog}
          onClose={handleCloseDialog}
        />
      )}
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <div className="tf-header-sm-dark flex-1">
            {suggestedArtifact.artifactType === ArtifactType.Feedback
              ? `Give ${label("feedback", {
                  titleCase: true,
                })} ${assigneeName ? `to ${assigneeName}` : ""}`
              : suggestedArtifact.artifactType === ArtifactType.Goal
              ? `Create ${label("goal", {
                  titleCase: true,
                })} ${assigneeName ? `for ${assigneeName}` : ""}`
              : suggestedArtifact.artifactType === ArtifactType.Recognition
              ? `Give ${label("recognition", {
                  titleCase: true,
                })} ${assigneeName ? `to ${assigneeName}` : ""}`
              : `Create ${label("action item", {
                  titleCase: true,
                })} ${assigneeName ? `for ${assigneeName}` : ""}`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              text={
                suggestedArtifact.artifactType === ArtifactType.Feedback
                  ? "Edit & Send"
                  : suggestedArtifact.artifactType === ArtifactType.Recognition
                  ? "Edit & Give"
                  : "Edit & Create"
              }
              disabled={loadingLink}
              onClick={() => setShowArtifactDialog(formOptions)}
              size={ButtonSize.medium}
              theme={buttonTheme.lightBlue}
            />
            <Button
              text="Start from scratch"
              disabled={loadingLink}
              onClick={() => {
                setShowArtifactDialog(
                  pick(formOptions, ["artifactType", "organizationId"])
                );
              }}
              size={ButtonSize.medium}
            />
          </div>
        </div>
        <div key={suggestedArtifact.id} className="flex group gap-2">
          <div className="mt-0.5">
            {suggestedCoreValue &&
              suggestedArtifact.artifactType === ArtifactType.Recognition && (
                <span
                  className={classNames(
                    "shrink-0 w-5",
                    !suggestedCoreValue?.image &&
                      "flex items-center justify-center rounded-full"
                  )}
                >
                  <CoreValueIcon
                    emoji={suggestedCoreValue?.emoji}
                    image={suggestedCoreValue?.image}
                    imagePxSize={20}
                  />
                </span>
              )}
            {suggestedArtifact.artifactType === ArtifactType.Feedback && (
              <span className="shrink-0">
                <TopicflowIcon
                  type="feedback"
                  className="text-gray-500 h-5 w-5"
                />
              </span>
            )}
            {suggestedArtifact.artifactType === ArtifactType.Goal && (
              <span className="shrink-0">
                <GoalIcon
                  state={suggestedArtifact.state}
                  scope={suggestedArtifact.scope}
                  isBasicPrivateGoal={
                    suggestedArtifact.visibility === GoalVisibility.Private
                  }
                  size="5"
                />
              </span>
            )}
            {suggestedArtifact.artifactType === ArtifactType.ActionItem && (
              <span className="shrink-0">
                <TopicflowIcon
                  type="actionItem"
                  className="text-gray-500 h-5 w-5"
                />
              </span>
            )}
          </div>
          <div className="flex-1">
            <button
              type="button"
              className="font-medium text-left block"
              onClick={() => setShowArtifactDialog(formOptions)}
              disabled={loadingLink}
            >
              {title}
              {suggestedArtifact.artifactType === ArtifactType.Goal &&
                suggestedArtifact.state === GoalState.Draft && (
                  <DraftLabel className="ml-1" />
                )}
            </button>
            {description && (
              <button
                type="button"
                className="text-gray-500 text-left block whitespace-pre-line"
                onClick={() => setShowArtifactDialog(formOptions)}
                disabled={loadingLink}
              >
                {description}
              </button>
            )}
          </div>
          {loadingLink && <Loading mini size="5" />}
        </div>
      </div>
    </>
  );
};

export default AskAISuggestedArtifact;
